import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BrandBtn, CloseX, Modal, Spinner } from '../../components/studio/studioUi';
import { useToast } from '../../components/common/Toast';
import { errorText, type VideoRender } from '../../services/video.service';
import { TIKTOK_PRIVACY_LABEL, socialService, type Placement, type PostOptions, type SocialAccount, type SocialConfig, type TiktokCreator } from '../../services/social.service';
import { FONT_SERIF } from '../landing/landingData';
import { FORMAT_OPTIONS, Segmented, fieldLabel, input } from './videoUi';
import { AccountAvatar, PlatformChip, TZ_LABEL, YoutubeConsent, toLocalInput } from './socialUi';

/**
 * "Posting ke sosmed": pick a finished render, the accounts, a caption, and
 * either now or a date. TikTok / Instagram only take 9:16; a landscape render
 * offers a portrait re-render instead of letting the user post it.
 */
export const ScheduleDialog: React.FC<{
    open: boolean;
    onClose: () => void;
    videoName: string;
    renders: VideoRender[];
    /** Version to preselect (opened from a render's menu); default: the newest. */
    initialRenderId?: string;
    renderCost: number;
    /** Queue a 9:16 render of this video (charged like any render). */
    onRenderPortrait: () => void;
    onScheduled: () => void;
}> = ({ open, onClose, videoName, renders, initialRenderId, renderCost, onRenderPortrait, onScheduled }) => {
    const navigate = useNavigate();
    const toast = useToast();
    const done = useMemo(() => renders.filter((r) => r.status === 'DONE' && r.url), [renders]);
    const [cfg, setCfg] = useState<SocialConfig | null>(null);
    const [accounts, setAccounts] = useState<SocialAccount[] | null>(null);
    const [renderId, setRenderId] = useState('');
    const [picked, setPicked] = useState<string[]>([]);
    const [caption, setCaption] = useState('');
    const [options, setOptions] = useState<Record<string, PostOptions>>({});
    const [creators, setCreators] = useState<Record<string, TiktokCreator | 'error'>>({});
    const [when, setWhen] = useState<'now' | 'later'>('later');
    const [at, setAt] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        socialService.config().then(setCfg).catch(() => setCfg(null));
        socialService.accounts().then(setAccounts).catch(() => setAccounts([]));
        setCaption((c) => c || videoName);
        const next = new Date(Date.now() + 60 * 60_000);
        next.setMinutes(0, 0, 0);
        setAt((v) => v || toLocalInput(next));
    }, [open, videoName]);

    useEffect(() => {
        if (open && initialRenderId) setRenderId(initialRenderId);
    }, [open, initialRenderId]);

    // Default to the newest finished render (a fresh 9:16 render lands first).
    useEffect(() => {
        if (!done.length || done.some((r) => r.id === renderId)) return;
        setRenderId(done[0].id);
    }, [done, renderId]);

    const render = done.find((r) => r.id === renderId);
    const vertical = (acc: SocialAccount) => !!cfg?.verticalOnly.includes(acc.platform);
    const feedOk = (acc: SocialAccount) => !vertical(acc) || !render?.format || render.format === 'portrait';
    const storyable = (acc: SocialAccount) => !!cfg?.stories.platforms.includes(acc.platform);
    /** Why this render cannot go to a Story ('' = it can). */
    const storyIssue = (() => {
        if (render?.format && render.format !== 'portrait') return 'Story hanya 9:16';
        const max = cfg?.stories.maxSeconds;
        if (max && render?.durationSec && render.durationSec > max + 0.5) return `Story maks ${max} dtk`;
        return '';
    })();
    const placementsFor = (acc: SocialAccount): Placement[] => (['feed', 'story'] as Placement[]).filter((p) => (p === 'feed' ? feedOk(acc) : storyable(acc) && !storyIssue));
    const blocked = (acc: SocialAccount) => (storyable(acc) ? placementsFor(acc).length === 0 : !feedOk(acc));
    const hasPortrait = done.some((r) => r.format === 'portrait');
    const portraitRendering = renders.some((r) => (r.status === 'QUEUED' || r.status === 'RENDERING') && r.format === 'portrait');

    const setOpt = (id: string, patch: PostOptions) => setOptions((o) => ({ ...o, [id]: { ...o[id], ...patch } }));
    const toggle = (acc: SocialAccount) => {
        if (picked.includes(acc.id)) return setPicked((p) => p.filter((x) => x !== acc.id));
        setPicked((p) => [...p, acc.id]);
        if (acc.platform === 'youtube' && !options[acc.id]?.title) setOpt(acc.id, { title: videoName, privacy: 'public' });
        // Reel by default; Story only when the feed is not possible for this render.
        if (storyable(acc)) setOpt(acc.id, { placements: placementsFor(acc).slice(0, 1) });
        // TikTok's rules: read the creator's options right before posting, no default privacy.
        if (acc.platform === 'tiktok' && !creators[acc.id]) {
            socialService.tiktokCreator(acc.id)
                .then((c) => setCreators((m) => ({ ...m, [acc.id]: c })))
                .catch((e) => { setCreators((m) => ({ ...m, [acc.id]: 'error' })); toast.error(errorText(e)); });
        }
    };

    // Render switched (format/length): drop placements and accounts it cannot go to.
    useEffect(() => {
        if (!accounts) return;
        setOptions((o) => Object.fromEntries(Object.entries(o).map(([id, v]) => {
            const a = accounts.find((x) => x.id === id);
            return [id, a && storyable(a) ? { ...v, placements: (v.placements ?? []).filter((p) => placementsFor(a).includes(p)) } : v];
        })));
        setPicked((p) => p.filter((id) => { const a = accounts.find((x) => x.id === id); return a && !blocked(a); }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [renderId, accounts]);

    const chosen = (accounts || []).filter((a) => picked.includes(a.id));
    const problem = (() => {
        if (!render) return 'Render video dulu.';
        if (!chosen.length) return 'Pilih minimal satu akun.';
        for (const a of chosen) {
            const o = options[a.id] || {};
            if (a.platform === 'youtube' && !o.title?.trim()) return 'Isi judul YouTube.';
            if (storyable(a) && !o.placements?.length) return `${a.name}: pilih Reel atau Story.`;
            if (a.platform === 'tiktok') {
                if (!o.privacy) return 'Pilih siapa yang bisa melihat video di TikTok.';
                if (o.brandedContent && o.privacy === 'SELF_ONLY') return 'Konten bermitra (branded) tidak bisa "Hanya saya" di TikTok.';
                const c = creators[a.id];
                if (c && c !== 'error' && render.durationSec && render.durationSec > c.maxDurationSec + 0.5) return `TikTok: video ${Math.round(render.durationSec)} dtk, akun ini maksimal ${c.maxDurationSec} dtk.`;
            }
        }
        if (when === 'later' && (!at || new Date(at).getTime() < Date.now())) return 'Pilih waktu di masa depan.';
        return '';
    })();

    const submit = async () => {
        if (problem || !render) return;
        setBusy(true);
        try {
            await socialService.schedule({
                renderId: render.id,
                accountIds: picked,
                caption,
                options: Object.fromEntries(picked.map((id) => [id, options[id] || {}])),
                scheduledAt: when === 'later' ? new Date(at).toISOString() : undefined,
            });
            toast.success(when === 'now' ? 'Sedang diposting. Statusnya muncul di "Jadwal posting".' : 'Terjadwal.');
            setPicked([]);
            onScheduled();
            onClose();
        } catch (e) {
            toast.error(errorText(e));
        } finally {
            setBusy(false);
        }
    };

    const anyProvider = cfg && Object.values(cfg.providers).some(Boolean);
    return (
        <Modal open={open} onClose={onClose} maxWidth={620} label="Posting ke media sosial">
            <div style={{ padding: '22px 24px 24px', display: 'grid', gap: 18 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontFamily: FONT_SERIF, fontSize: 28, lineHeight: 1.1 }}>Posting ke media sosial</div>
                        <div style={{ marginTop: 6, fontSize: 13.5, color: 'var(--hv-muted)' }}>Langsung dari HyVay, tanpa unduh lalu unggah satu per satu.</div>
                    </div>
                    <CloseX onClick={onClose} />
                </div>

                {cfg && !cfg.enabled && <div style={{ fontSize: 13.5, color: 'var(--hv-danger)' }}>Penjadwalan belum tersedia di server ini.</div>}

                <div>
                    <div style={fieldLabel}>Video</div>
                    {!done.length && <div style={{ fontSize: 13, color: 'var(--hv-muted)' }}>Belum ada hasil render. Klik <b>Render MP4</b> dulu.</div>}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {done.map((r) => {
                            const v = renders.length - renders.findIndex((x) => x.id === r.id);
                            return (
                                <button key={r.id} type="button" onClick={() => setRenderId(r.id)} style={{ padding: '8px 12px', borderRadius: 12, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, color: 'var(--hv-ink)', background: 'var(--hv-card)', border: `2px solid ${r.id === renderId ? 'var(--brand,#13422C)' : 'var(--hv-line)'}` }}>
                                    <b>v{v}</b> · {FORMAT_OPTIONS.find((f) => f.value === r.format)?.label ?? '?'}{r.durationSec ? ` · ${Math.round(r.durationSec)} dtk` : ''}
                                </button>
                            );
                        })}
                    </div>
                    {render?.url && (
                        // What will be posted, exactly (TikTok's Direct Post rules ask for a preview).
                        <video
                            key={render.id}
                            src={render.url}
                            controls
                            muted
                            playsInline
                            preload="metadata"
                            style={{ display: 'block', marginTop: 10, maxWidth: '100%', maxHeight: 280, aspectRatio: render.format === 'portrait' ? '9 / 16' : render.format === 'square' ? '1 / 1' : '16 / 9', borderRadius: 12, background: '#000' }}
                        />
                    )}
                </div>

                <div>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                        <div style={fieldLabel}>Akun</div>
                        <button type="button" onClick={() => navigate('/video/schedule')} style={{ border: 0, background: 'transparent', cursor: 'pointer', fontSize: 12.5, fontWeight: 700, color: 'var(--hv-ink)', fontFamily: 'inherit' }}>Kelola akun →</button>
                    </div>
                    {accounts === null && <Spinner />}
                    {accounts?.length === 0 && (
                        <div style={{ fontSize: 13, color: 'var(--hv-muted)' }}>
                            Belum ada akun terhubung. {anyProvider ? <button type="button" onClick={() => navigate('/video/schedule')} style={{ border: 0, background: 'transparent', padding: 0, cursor: 'pointer', fontWeight: 700, color: 'var(--hv-ink)', fontFamily: 'inherit', fontSize: 13 }}>Hubungkan akun</button> : 'Integrasi belum dikonfigurasi di server.'}
                        </div>
                    )}
                    <div style={{ display: 'grid', gap: 8 }}>
                        {accounts?.map((a) => {
                            const off = blocked(a) || a.status !== 'ACTIVE';
                            const on = picked.includes(a.id);
                            return (
                                <div key={a.id} style={{ borderRadius: 14, border: `1.5px solid ${on ? 'var(--brand,#13422C)' : 'var(--hv-line)'}`, background: 'var(--hv-card)', opacity: off ? 0.65 : 1 }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', cursor: off ? 'not-allowed' : 'pointer' }}>
                                        <input type="checkbox" disabled={off} checked={on} onChange={() => toggle(a)} />
                                        <AccountAvatar account={a} />
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: 13.5, fontWeight: 700 }}>{a.name}</div>
                                            <PlatformChip platform={a.platform} />
                                        </div>
                                        {a.status !== 'ACTIVE' && <span style={{ fontSize: 12, color: 'var(--hv-danger)' }}>Hubungkan ulang</span>}
                                        {blocked(a) && a.status === 'ACTIVE' && <span style={{ fontSize: 12, color: 'var(--hv-muted)' }}>Hanya 9:16</span>}
                                    </label>
                                    {on && storyable(a) && (
                                        <PlacementPicker
                                            platform={a.platform}
                                            value={options[a.id]?.placements ?? []}
                                            feed={feedOk(a) ? '' : 'Hanya 9:16'}
                                            story={storyIssue}
                                            portrait={render?.format === 'portrait'}
                                            onChange={(placements) => setOpt(a.id, { placements })}
                                        />
                                    )}
                                    {on && <PlatformFields account={a} value={options[a.id] || {}} onChange={(p) => setOpt(a.id, p)} creator={creators[a.id]} />}
                                </div>
                            );
                        })}
                    </div>
                    {render && render.format && render.format !== 'portrait' && accounts?.some(vertical) && (
                        <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 12, background: 'var(--hv-page-2)', fontSize: 12.5, color: 'var(--hv-body)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            <span style={{ flex: 1, minWidth: 200 }}>TikTok dan Instagram Reels butuh video tegak 9:16.{hasPortrait ? ' Pilih versi 9:16 di atas, atau buat yang baru.' : ''}</span>
                            <button type="button" disabled={portraitRendering} onClick={onRenderPortrait} style={{ height: 32, padding: '0 12px', borderRadius: 999, border: '1px solid var(--hv-line)', background: 'var(--hv-card)', cursor: portraitRendering ? 'default' : 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, color: 'var(--hv-ink)' }}>
                                {portraitRendering ? 'Merender 9:16…' : `Buat versi 9:16 · ${renderCost} credit`}
                            </button>
                        </div>
                    )}
                </div>

                <label style={{ display: 'block' }}>
                    <div style={fieldLabel}>Caption</div>
                    <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={4} placeholder="Tulis caption, hashtag, ajakan…" style={{ ...input, resize: 'vertical', lineHeight: 1.5 }} />
                    <div style={{ marginTop: 4, fontSize: 11.5, color: 'var(--hv-muted)', textAlign: 'right' }}>{caption.length} / 2200 (batas Instagram & TikTok){chosen.some((a) => a.platform === 'threads') && cfg?.maxText.threads ? ` · Threads memakai ${cfg.maxText.threads} karakter pertama` : ''}</div>
                </label>

                <div>
                    <div style={fieldLabel}>Kapan</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <Segmented value={when} onChange={setWhen} options={[{ value: 'later', label: 'Jadwalkan' }, { value: 'now', label: 'Sekarang' }]} />
                        {when === 'later' && (
                            <>
                                <input type="datetime-local" value={at} min={toLocalInput(new Date())} max={cfg ? toLocalInput(new Date(Date.now() + cfg.maxAheadDays * 86400_000)) : undefined} onChange={(e) => setAt(e.target.value)} style={{ ...input, width: 'auto' }} />
                                {TZ_LABEL && <span style={{ fontSize: 12, color: 'var(--hv-muted)' }}>{TZ_LABEL}</span>}
                            </>
                        )}
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    {problem && <span style={{ fontSize: 12.5, color: 'var(--hv-muted)' }}>{problem}</span>}
                    <BrandBtn data-track="schedule_submit" onClick={submit} disabled={!!problem || busy || !cfg?.enabled}>
                        {busy ? <Spinner light /> : when === 'now' ? `Posting ke ${chosen.length || ''} akun` : `Jadwalkan ${chosen.length || ''} post`}
                    </BrandBtn>
                </div>
            </div>
        </Modal>
    );
};

/** Per-platform fields under a picked account. */
const PlatformFields: React.FC<{ account: SocialAccount; value: PostOptions; onChange: (p: PostOptions) => void; creator?: TiktokCreator | 'error' }> = ({ account, value, onChange, creator }) => {
    const box: React.CSSProperties = { padding: '0 12px 12px 40px', display: 'grid', gap: 8 };
    const small: React.CSSProperties = { fontSize: 12, color: 'var(--hv-muted)' };
    if (account.platform === 'youtube') {
        return (
            <div style={box}>
                <label style={small}>Judul YouTube
                    <input value={value.title ?? ''} maxLength={100} onChange={(e) => onChange({ title: e.target.value })} style={{ ...input, marginTop: 4 }} />
                </label>
                <label style={small}>Visibilitas
                    <select value={value.privacy ?? 'public'} onChange={(e) => onChange({ privacy: e.target.value })} style={{ ...input, marginTop: 4 }}>
                        <option value="public">Publik</option>
                        <option value="unlisted">Tidak publik (hanya yang punya link)</option>
                        <option value="private">Pribadi</option>
                    </select>
                </label>
                <div style={small}>Video 9:16 otomatis tampil sebagai Shorts.</div>
                <YoutubeConsent />
            </div>
        );
    }
    if (account.platform === 'tiktok') {
        if (!creator) return <div style={box}><Spinner size={14} /></div>;
        if (creator === 'error') return <div style={{ ...box, color: 'var(--hv-danger)', fontSize: 12.5 }}>Gagal membaca akun TikTok. Coba hubungkan ulang.</div>;
        const check = (key: 'allowComment' | 'allowDuet' | 'allowStitch', text: string, disabled: boolean) => (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: disabled ? 'var(--hv-muted)' : 'var(--hv-ink)' }}>
                <input type="checkbox" disabled={disabled} checked={!disabled && value[key] !== false} onChange={(e) => onChange({ [key]: e.target.checked })} />
                {text}{disabled ? ' (dimatikan di akun TikTok)' : ''}
            </label>
        );
        return (
            <div style={box}>
                <div style={small}>Diposting sebagai <b style={{ color: 'var(--hv-ink)' }}>{creator.nickname}</b> · maks {creator.maxDurationSec} dtk</div>
                {!creator.audited && (
                    <div style={{ padding: '8px 10px', borderRadius: 10, background: 'var(--hv-sand)', color: 'var(--hv-sand-ink)', fontSize: 12.5, lineHeight: 1.5 }}>
                        App HyVay belum lolos audit TikTok: untuk sementara video hanya bisa <b>Hanya saya</b>, dan akun TikTok-mu harus diset <b>Private</b>.
                    </div>
                )}
                <label style={small}>Siapa yang bisa melihat
                    <select value={value.privacy ?? ''} onChange={(e) => onChange({ privacy: e.target.value })} style={{ ...input, marginTop: 4 }}>
                        <option value="" disabled>Pilih…</option>
                        {creator.privacyOptions.map((p) => <option key={p} value={p}>{TIKTOK_PRIVACY_LABEL[p] ?? p}</option>)}
                    </select>
                </label>
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                    {check('allowComment', 'Komentar', creator.commentDisabled)}
                    {check('allowDuet', 'Duet', creator.duetDisabled)}
                    {check('allowStitch', 'Stitch', creator.stitchDisabled)}
                </div>
                <div style={{ display: 'grid', gap: 4 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                        <input type="checkbox" checked={!!value.brandOrganic} onChange={(e) => onChange({ brandOrganic: e.target.checked })} />
                        Promosi bisnis saya sendiri (label "Promotional content")
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                        <input type="checkbox" checked={!!value.brandedContent} onChange={(e) => onChange({ brandedContent: e.target.checked })} />
                        Kerja sama berbayar dengan brand lain (label "Paid partnership")
                    </label>
                </div>
                <div style={small}>
                    Dengan memposting, kamu menyetujui <a href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en" target="_blank" rel="noreferrer">Music Usage Confirmation</a>
                    {value.brandedContent && <> dan <a href="https://www.tiktok.com/legal/page/global/bc-policy/en" target="_blank" rel="noreferrer">Branded Content Policy</a></>} TikTok.
                </div>
            </div>
        );
    }
    if (account.platform === 'linkedin' || account.platform === 'facebook') {
        return (
            <div style={box}>
                <label style={small}>Judul video (opsional)
                    <input value={value.title ?? ''} maxLength={200} onChange={(e) => onChange({ title: e.target.value })} style={{ ...input, marginTop: 4 }} />
                </label>
            </div>
        );
    }
    if (account.platform === 'threads') return <div style={{ ...box, ...small }}>Diposting sebagai video di profil Threads, dengan caption sebagai teksnya.</div>;
    return null;
};

/** Facebook/Instagram: feed (Reel / Page video) and/or Story; each becomes its own post. */
const PlacementPicker: React.FC<{ platform: string; value: Placement[]; feed: string; story: string; portrait: boolean; onChange: (v: Placement[]) => void }> = ({ platform, value, feed, story, portrait, onChange }) => {
    const feedLabel = platform === 'facebook' && !portrait ? 'Video Page' : 'Reel';
    const item = (p: Placement, text: string, issue: string) => (
        <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: issue ? 'var(--hv-muted)' : 'var(--hv-ink)', cursor: issue ? 'not-allowed' : 'pointer' }}>
            <input type="checkbox" disabled={!!issue} checked={!issue && value.includes(p)} onChange={(e) => onChange(e.target.checked ? [...value.filter((x) => x !== p), p] : value.filter((x) => x !== p))} />
            {text}{issue ? ` (${issue})` : ''}
        </label>
    );
    return (
        <div style={{ padding: '0 12px 8px 40px', display: 'grid', gap: 6 }}>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {item('feed', feedLabel, feed)}
                {item('story', 'Story', story)}
            </div>
            {value.includes('story') && <div style={{ fontSize: 12, color: 'var(--hv-muted)' }}>Caption tidak ditampilkan di Story. Reel dan Story dikirim sebagai dua post terpisah.</div>}
        </div>
    );
};

